import { useMemo } from 'react';
import { css } from '@emotion/react';

import { MentoringProgramRole } from 'redux/schemas/models/mentoring-program-enrollments';
import {
  MentoringProgramReflectionParticipantResponses,
  MentoringProgramReflectionQuestion,
} from 'redux/schemas/models/mentoring-program-reflections';
import { User } from 'redux/schemas/models/my-account';
import { standardSpacing } from 'styles/global_defaults/scaffolding';
import ReflectionResponseCard from './reflection-response-card';

const questionGroupStyles = css`
  gap: ${standardSpacing}px;
`;

type ReflectionReadViewProps = {
  questions: MentoringProgramReflectionQuestion[];
  currentEnrollmentResponses: MentoringProgramReflectionParticipantResponses;
  connectionEnrollmentResponses: MentoringProgramReflectionParticipantResponses | null;
  currentEnrollmentUser: User | undefined;
  currentEnrollmentRole: MentoringProgramRole;
  connectionEnrollmentUser: User | undefined;
  connectionEnrollmentRole: MentoringProgramRole;
};

const ReflectionReadView = ({
  questions,
  currentEnrollmentResponses,
  connectionEnrollmentResponses,
  currentEnrollmentUser,
  currentEnrollmentRole,
  connectionEnrollmentUser,
  connectionEnrollmentRole,
}: ReflectionReadViewProps) => {
  const sortedQuestions = useMemo(
    () => [...questions].sort((a, b) => a.questionIndex - b.questionIndex),
    [questions],
  );

  const currentEnrollmentResponsesByQuestionId = useMemo(
    () => Object.fromEntries(currentEnrollmentResponses.answers.map(a => [a.questionId, a.response])),
    [currentEnrollmentResponses],
  );

  const connectionEnrollmentResponsesByQuestionId = useMemo(
    () => Object.fromEntries((connectionEnrollmentResponses?.answers ?? []).map(a => [a.questionId, a.response])),
    [connectionEnrollmentResponses],
  );

  const isCurrentEnrollmentMentor =
    currentEnrollmentRole === MentoringProgramRole.MENTORSHIP_PROGRAM_MENTOR;
  const mentorUser = isCurrentEnrollmentMentor ? currentEnrollmentUser : connectionEnrollmentUser;
  const menteeUser = isCurrentEnrollmentMentor ? connectionEnrollmentUser : currentEnrollmentUser;

  return (
    <>
      {sortedQuestions.map(({ id, questionText }, questionIndex) => {
        const mentorAnswer = isCurrentEnrollmentMentor
          ? currentEnrollmentResponsesByQuestionId[id]
          : connectionEnrollmentResponsesByQuestionId[id];
        const menteeAnswer = isCurrentEnrollmentMentor
          ? connectionEnrollmentResponsesByQuestionId[id]
          : currentEnrollmentResponsesByQuestionId[id];

        return (
          <div key={id} css={questionGroupStyles} className='d-flex flex-column'>
            {mentorAnswer && mentorUser && (
              <ReflectionResponseCard
                questionText={questionText}
                response={mentorAnswer}
                user={mentorUser}
                role={MentoringProgramRole.MENTORSHIP_PROGRAM_MENTOR}
                index={questionIndex}
              />
            )}
            {menteeAnswer && menteeUser && (
              <ReflectionResponseCard
                questionText={questionText}
                response={menteeAnswer}
                user={menteeUser}
                role={MentoringProgramRole.MENTORSHIP_PROGRAM_MENTEE}
                index={questionIndex}
              />
            )}
          </div>
        );
      })}
    </>
  );
};

export default ReflectionReadView;
